// src/cockpit4/components/QuoteNegotiator.tsx
// ============================================
// P1-功能5：智能报价与谈判助手（客户中心）
//
// 功能：
//   1. 基于历史数据的智能定价建议（分析现有客户成交价，建议报价区间）
//   2. 谈判策略建议（客户心理/竞品分析/底线建议）
//   3. 报价单自动生成（生成 Markdown 报价单，写入 Vault：XEAOS/CRM/Quotes/{客户名}-{date}.md）
//   4. 成交概率预测（基于客户阶段/跟进次数/意向分预测）
//   5. 谈判记录和复盘（记录每次谈判结果，复盘分析）
//
// 数据来源：
//   - RuntimeManager.query(RuntimeName.CRM, { type: "list" }) 获取客户
//   - localStorage（key: xeaos_quotes）持久化报价记录
//   - 无数据时显示空状态提示，绝不展示假数据
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calculator,
  RefreshCw,
  Loader2,
  FileText,
  Target,
  Brain,
  Save,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Users,
  DollarSign,
  Scale,
  Plus,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import type { CustomerProfile, CustomerTier } from "../../runtime/CRMRuntime/types";

// ============================================
// 类型与常量
// ============================================

const QUOTES_STORAGE_KEY = "xeaos_quotes";

/** 报价状态 */
type QuoteStatus = "draft" | "sent" | "accepted" | "rejected" | "negotiating";

/** 谈判记录 */
interface NegotiationRecord {
  id: string;
  quoteId: string;
  date: string;
  round: number;
  ourOffer: number;
  customerCounter: number;
  outcome: "pending" | "accepted" | "rejected" | "continue";
  note: string;
}

/** 报价记录 */
interface QuoteRecord {
  id: string;
  customerId: string;
  customerName: string;
  industry: string;
  tier: CustomerTier;
  amount: number;
  status: QuoteStatus;
  createdAt: string;
  validUntil: string;
  vaultPath?: string;
  winProbability: number;
  suggestedRange: { low: number; mid: number; high: number };
  strategy: string;
  bottomLine: number;
  negotiations: NegotiationRecord[];
  reviewNote: string;
}

// 等级权重（用于成交概率与定价参考）
const TIER_WEIGHT: Record<CustomerTier, number> = {
  "S+": 1.2,
  S: 1.1,
  A: 1.0,
  B: 0.9,
  C: 0.8,
};

const STATUS_LABEL: Record<QuoteStatus, string> = {
  draft: "草稿",
  sent: "已发送",
  accepted: "已成交",
  rejected: "已拒绝",
  negotiating: "谈判中",
};

const STATUS_COLOR: Record<QuoteStatus, string> = {
  draft: "#9CA3AF",
  sent: "#60A5FA",
  accepted: "#34D399",
  rejected: "#F87171",
  negotiating: "#FB923C",
};

// ============================================
// 工具函数
// ============================================

const DAY_MS = 24 * 60 * 60 * 1000;

function genId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysSince(dateStr: string): number {
  if (!dateStr) return -1;
  const t = new Date(dateStr).getTime();
  if (isNaN(t)) return -1;
  return Math.floor((Date.now() - t) / DAY_MS);
}

function formatAmount(amount: number): string {
  if (!amount || amount <= 0) return "¥0";
  if (amount >= 10000) return `¥${(amount / 10000).toFixed(1)}w`;
  return `¥${amount.toLocaleString()}`;
}

/** 安全读取 localStorage */
function loadQuotes(): QuoteRecord[] {
  try {
    const raw = localStorage.getItem(QUOTES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as QuoteRecord[]) : [];
  } catch {
    return [];
  }
}

/** 安全写入 localStorage */
function saveQuotes(quotes: QuoteRecord[]): void {
  try {
    localStorage.setItem(QUOTES_STORAGE_KEY, JSON.stringify(quotes));
  } catch {
    // 容量超限或隐私模式：忽略
  }
}

/**
 * 基于历史成交价计算建议报价区间
 * - 提取现有已成交客户的 estimatedValue 作为基准
 * - 按 tier 权重调整
 * - 返回低/中/高三档
 */
function suggestPriceRange(
  customer: CustomerProfile,
  history: CustomerProfile[]
): { low: number; mid: number; high: number } {
  // 已成交客户的历史金额
  const closedDeals = history.filter(
    (c) => c.followup?.status === "closed-won" || c.followup?.status === "active"
  );
  const values = closedDeals
    .map((c) => c.followup?.estimatedValue || 0)
    .filter((v) => v > 0);

  let base = 0;
  if (values.length > 0) {
    // 中位数作为基准
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    base = sorted.length % 2 === 0 ? Math.round((sorted[mid - 1] + sorted[mid]) / 2) : sorted[mid];
  } else {
    // 无历史成交：用客户预估金额
    base = customer.followup?.estimatedValue || 50000;
  }

  const tierWeight = TIER_WEIGHT[customer.tier] || 1.0;
  const adjusted = Math.round(base * tierWeight);

  return {
    low: Math.round(adjusted * 0.85),
    mid: adjusted,
    high: Math.round(adjusted * 1.15),
  };
}

/**
 * 成交概率预测（0-100）
 * 维度：
 *   - 客户阶段（closed-won/active 100, negotiating 70, prospecting 40）
 *   - 跟进次数（history 条目数）
 *   - tier 等级
 *   - 最近联系（7天内 +15，14天内 +10，30天内 +5）
 */
function predictWinProbability(customer: CustomerProfile): number {
  const status = customer.followup?.status;
  let base = 40;
  if (status === "closed-won" || status === "active") base = 100;
  else if (status === "negotiating") base = 70;
  else base = 40;

  // 跟进次数
  const historyCount = customer.history
    ? (customer.history.match(/-\s*\d{4}-\d{2}-\d{2}\s*:/g) || []).length
    : 0;
  base += Math.min(15, historyCount * 3);

  // tier
  const tierBonus: Record<CustomerTier, number> = { "S+": 10, S: 8, A: 5, B: 2, C: 0 };
  base += tierBonus[customer.tier] || 0;

  // 最近联系
  const d = daysSince(customer.followup?.lastContact || "");
  if (d >= 0) {
    if (d <= 7) base += 15;
    else if (d <= 14) base += 10;
    else if (d <= 30) base += 5;
    else base -= 5;
  }

  return Math.max(0, Math.min(100, base));
}

/** 生成谈判策略建议 */
function generateStrategy(customer: CustomerProfile, range: { low: number; mid: number; high: number }): {
  strategy: string;
  bottomLine: number;
} {
  const tier = customer.tier;
  const industry = customer.industry || "通用";
  const parts: string[] = [];

  // 客户心理
  if (tier === "S+" || tier === "S") {
    parts.push(`【客户心理】${tier} 级客户决策链长、关注 ROI 与长期价值，需提供量化收益测算，避免单纯价格战。`);
  } else if (tier === "A") {
    parts.push(`【客户心理】A 级客户关注性价比与同行案例，建议以案例 + 试用降低决策门槛。`);
  } else {
    parts.push(`【客户心理】${tier} 级客户价格敏感，可分阶段报价或提供基础版起步。`);
  }

  // 竞品分析
  parts.push(`【竞品分析】${industry} 行业存在多家竞品，建议突出差异化（交付速度/服务响应/定制能力），避免陷入同质化比价。`);

  // 报价策略
  parts.push(`【报价策略】首报价建议 ${formatAmount(range.high)}（高位锚定），预留 15% 让步空间，分 2-3 轮让步，每次让步幅度递减。`);

  // 底线
  const bottomLine = Math.round(range.low * 0.95);
  parts.push(`【底线建议】最低可接受 ${formatAmount(bottomLine)}（低于此价需特批），低于 ${formatAmount(range.low)} 时考虑附加条件（如缩短交付周期/捆绑后续服务）。`);

  return {
    strategy: parts.join("\n"),
    bottomLine,
  };
}

/** 生成报价单 Markdown */
function generateQuoteMarkdown(
  customer: CustomerProfile,
  quote: QuoteRecord
): string {
  const validUntil = quote.validUntil;
  return `# 报价单 · ${customer.company}

> 由 XEAOS 客户中心 · 智能报价助手生成
> 生成日期：${quote.createdAt} · 有效期至：${validUntil}

## 客户信息

| 项目 | 内容 |
| --- | --- |
| 公司名称 | ${customer.company} |
| 行业 | ${customer.industry || "—"} |
| 地区 | ${customer.region || "—"} |
| 价值等级 | ${customer.tier} |
| 负责人 | ${customer.owner} |
| 联系人 | ${customer.contact.name || "—"} / ${customer.contact.phone || "—"} |

## 报价明细

| 项目 | 数量 | 单价 | 金额 |
| --- | --- | --- | --- |
| 服务/产品总报价 | 1 | ${formatAmount(quote.amount)} | ${formatAmount(quote.amount)} |
| **合计** |  |  | **${formatAmount(quote.amount)}** |

## 智能定价建议

- 建议报价区间：${formatAmount(quote.suggestedRange.low)} ~ ${formatAmount(quote.suggestedRange.high)}（中位 ${formatAmount(quote.suggestedRange.mid)}）
- 成交概率预测：${quote.winProbability}%
- 底线价格：${formatAmount(quote.bottomLine)}

## 谈判策略

${quote.strategy}

## 谈判记录

${quote.negotiations.length === 0 ? "（暂无谈判记录）" : quote.negotiations.map((n) => `- 第 ${n.round} 轮（${n.date}）：我方报价 ${formatAmount(n.ourOffer)}，客户还价 ${formatAmount(n.customerCounter)}，结果 ${n.outcome}。${n.note}`).join("\n")}

## 复盘备注

${quote.reviewNote || "（暂无复盘）"}

---
*本报价单由 XEAOS 自动生成，最终以双方签署的正式合同为准。*
`;
}

/** 通过 /vault-api/write 写入 Vault */
async function writeVaultQuote(path: string, content: string): Promise<void> {
  const resp = await fetch("/vault-api/write", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ path, content }),
  });
  const data = await resp.json().catch(() => null);
  if (!resp.ok || !data?.success) {
    throw new Error(data?.error || `写入 Vault 失败（HTTP ${resp.status}）`);
  }
}

// ============================================
// 主组件
// ============================================

export default function QuoteNegotiator() {
  const [customers, setCustomers] = useState<CustomerProfile[]>([]);
  const [quotes, setQuotes] = useState<QuoteRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 选中的客户（用于生成报价）
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  // 展开的报价 ID（用于查看谈判记录）
  const [expandedQuoteId, setExpandedQuoteId] = useState<string | null>(null);
  // 新增谈判记录的草稿（按 quoteId）
  const [negoDrafts, setNegoDrafts] = useState<Record<string, { counter: string; note: string }>>({});
  // 生成报价中
  const [generating, setGenerating] = useState(false);
  // 生成反馈
  const [genMsg, setGenMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // 加载真实客户列表
  const loadCustomers = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) break;
        await new Promise((r) => setTimeout(r, 300));
      }
      if (!RuntimeManager.isInitialized()) {
        setError("RuntimeManager 尚未初始化");
        setLoading(false);
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.CRM, { type: "list" });
      if (resp?.success && resp.data) {
        const inner = resp.data as { data?: CustomerProfile[] } | CustomerProfile[];
        const list = Array.isArray(inner) ? inner : inner?.data || [];
        setCustomers(Array.isArray(list) ? list : []);
      } else {
        setError(resp?.error || "查询客户列表失败");
      }
    } catch (err) {
      setError((err as Error).message || "加载客户列表异常");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 初次挂载
  useEffect(() => {
    setQuotes(loadQuotes());
    void loadCustomers();
  }, [loadCustomers]);

  // 报价变化时持久化
  useEffect(() => {
    saveQuotes(quotes);
  }, [quotes]);

  // 选中客户
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === selectedCustomerId) || null,
    [customers, selectedCustomerId]
  );

  // 派生：建议报价区间 + 策略 + 成交概率
  const suggestion = useMemo(() => {
    if (!selectedCustomer) return null;
    const range = suggestPriceRange(selectedCustomer, customers);
    const { strategy, bottomLine } = generateStrategy(selectedCustomer, range);
    const winProbability = predictWinProbability(selectedCustomer);
    return { range, strategy, bottomLine, winProbability };
  }, [selectedCustomer, customers]);

  // 生成报价单并写入 Vault
  const generateQuote = useCallback(async () => {
    if (!selectedCustomer || !suggestion) return;
    setGenerating(true);
    setGenMsg(null);
    try {
      const today = todayStr();
      const validUntilDate = new Date();
      validUntilDate.setDate(validUntilDate.getDate() + 30);
      const validUntil = validUntilDate.toISOString().slice(0, 10);

      const quoteId = genId("quote");
      const quote: QuoteRecord = {
        id: quoteId,
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.company,
        industry: selectedCustomer.industry || "—",
        tier: selectedCustomer.tier,
        amount: suggestion.range.mid,
        status: "draft",
        createdAt: today,
        validUntil,
        winProbability: suggestion.winProbability,
        suggestedRange: suggestion.range,
        strategy: suggestion.strategy,
        bottomLine: suggestion.bottomLine,
        negotiations: [],
        reviewNote: "",
      };

      // 生成 Markdown 并写入 Vault
      const safeName = selectedCustomer.company.replace(/[\\/:*?"<>|]/g, "_");
      const vaultPath = `XEAOS/CRM/Quotes/${safeName}-${today}.md`;
      const markdown = generateQuoteMarkdown(selectedCustomer, quote);
      await writeVaultQuote(vaultPath, markdown);

      quote.vaultPath = vaultPath;
      setQuotes((prev) => [quote, ...prev]);
      setGenMsg({ ok: true, text: `已生成报价单并写入 Vault：${vaultPath}` });
      setSelectedCustomerId("");
    } catch (err) {
      setGenMsg({ ok: false, text: `生成失败：${(err as Error).message || String(err)}` });
    } finally {
      setGenerating(false);
    }
  }, [selectedCustomer, suggestion]);

  /** 更新报价状态 */
  const updateQuoteStatus = useCallback((quoteId: string, status: QuoteStatus) => {
    setQuotes((prev) => prev.map((q) => (q.id === quoteId ? { ...q, status } : q)));
  }, []);

  /** 新增谈判记录 */
  const addNegotiation = useCallback((quoteId: string) => {
    const draft = negoDrafts[quoteId];
    if (!draft) return;
    const counter = Number(draft.counter);
    if (!counter || counter <= 0) return;
    setQuotes((prev) =>
      prev.map((q) => {
        if (q.id !== quoteId) return q;
        const round = q.negotiations.length + 1;
        const record: NegotiationRecord = {
          id: genId("nego"),
          quoteId,
          date: todayStr(),
          round,
          ourOffer: q.amount,
          customerCounter: counter,
          outcome: "continue",
          note: draft.note.trim(),
        };
        // 更新当前报价为客户还价与原报价的中点
        const newAmount = Math.round((q.amount + counter) / 2);
        return {
          ...q,
          amount: newAmount,
          status: "negotiating",
          negotiations: [...q.negotiations, record],
        };
      })
    );
    setNegoDrafts((s) => ({ ...s, [quoteId]: { counter: "", note: "" } }));
  }, [negoDrafts]);

  /** 更新复盘备注 */
  const updateReview = useCallback((quoteId: string, reviewNote: string) => {
    setQuotes((prev) => prev.map((q) => (q.id === quoteId ? { ...q, reviewNote } : q)));
  }, []);

  /** 删除报价 */
  const removeQuote = useCallback((quoteId: string) => {
    setQuotes((prev) => prev.filter((q) => q.id !== quoteId));
  }, []);

  // 统计概览
  const overview = useMemo(() => {
    const total = quotes.length;
    const accepted = quotes.filter((q) => q.status === "accepted").length;
    const negotiating = quotes.filter((q) => q.status === "negotiating").length;
    const totalAmount = quotes.reduce((s, q) => s + q.amount, 0);
    const avgWinProb = total > 0 ? Math.round(quotes.reduce((s, q) => s + q.winProbability, 0) / total) : 0;
    return { total, accepted, negotiating, totalAmount, avgWinProb };
  }, [quotes]);

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md flex items-center justify-center bg-amber-500/15 border border-amber-500/30">
            <Calculator size={13} className="text-amber-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">智能报价与谈判助手</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              Quote Negotiator · {overview.total} 报价 · 成交 {overview.accepted} · 谈判中 {overview.negotiating}
            </p>
          </div>
        </div>
        <button
          onClick={loadCustomers}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
        >
          {refreshing ? (
            <Loader2 size={11} className="text-amber-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-amber-400" />
          )}
          <span className="text-[9px] font-mono text-amber-300 uppercase tracking-wider">
            {refreshing ? "刷新中..." : "刷新客户"}
          </span>
        </button>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div className="px-3 py-2 rounded-md border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[10px] font-mono">
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center">
          <Loader2 size={20} className="mx-auto text-amber-400 animate-spin" />
          <p className="text-[10px] font-mono text-gray-600 mt-2">加载客户数据...</p>
        </div>
      ) : (
        <>
          {/* 顶部 KPI */}
          <div className="grid grid-cols-4 gap-2 p-4 border-b border-white/[0.04]">
            <KpiCard icon={FileText} label="报价总数" value={String(overview.total)} color="#FBBF24" />
            <KpiCard icon={CheckCircle2} label="已成交" value={String(overview.accepted)} color="#34D399" />
            <KpiCard icon={DollarSign} label="报价总金额" value={formatAmount(overview.totalAmount)} color="#38BDF8" />
            <KpiCard icon={Target} label="平均成交概率" value={`${overview.avgWinProb}%`} color="#A78BFA" />
          </div>

          {/* 新建报价：选择客户 */}
          <div className="p-4 border-b border-white/[0.04]">
            <div className="flex items-center gap-2 mb-2">
              <Brain size={11} className="text-amber-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                智能定价建议（选择客户生成报价单）
              </span>
            </div>
            {customers.length === 0 ? (
              <p className="text-[11px] font-mono text-gray-600 text-center py-3">暂无客户数据</p>
            ) : (
              <div className="space-y-3">
                <select
                  value={selectedCustomerId}
                  onChange={(e) => setSelectedCustomerId(e.target.value)}
                  className="w-full px-2 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-gray-200 font-mono focus:outline-none focus:border-amber-500/40"
                >
                  <option value="">— 选择客户 —</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.company} · {c.tier} · {c.industry || "—"}
                    </option>
                  ))}
                </select>

                {/* 建议卡片 */}
                {selectedCustomer && suggestion && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-md border border-amber-500/20 bg-amber-500/[0.04] p-3 space-y-2"
                  >
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded bg-white/[0.03] border border-white/[0.04] p-2">
                        <div className="text-[9px] font-mono text-gray-500 uppercase">建议报价区间</div>
                        <div className="text-[12px] font-mono text-amber-300 mt-0.5">
                          {formatAmount(suggestion.range.low)} ~ {formatAmount(suggestion.range.high)}
                        </div>
                        <div className="text-[9px] font-mono text-gray-500 mt-0.5">
                          中位：{formatAmount(suggestion.range.mid)}
                        </div>
                      </div>
                      <div className="rounded bg-white/[0.03] border border-white/[0.04] p-2">
                        <div className="text-[9px] font-mono text-gray-500 uppercase">成交概率 / 底线</div>
                        <div className="text-[12px] font-mono text-emerald-300 mt-0.5">
                          {suggestion.winProbability}%
                        </div>
                        <div className="text-[9px] font-mono text-red-400 mt-0.5">
                          底线：{formatAmount(suggestion.bottomLine)}
                        </div>
                      </div>
                    </div>
                    <div className="rounded bg-white/[0.03] border border-white/[0.04] p-2">
                      <div className="text-[9px] font-mono text-gray-500 uppercase mb-1">谈判策略</div>
                      <pre className="text-[10px] font-mono text-gray-300 whitespace-pre-wrap leading-snug">
                        {suggestion.strategy}
                      </pre>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedCustomerId("")}
                        className="px-2 py-1 rounded text-[9px] font-mono text-gray-400 hover:text-gray-200"
                      >
                        取消
                      </button>
                      <button
                        onClick={() => void generateQuote()}
                        disabled={generating}
                        className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-500/15 border border-amber-500/30 text-amber-300 hover:bg-amber-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {generating ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                        生成报价单
                      </button>
                    </div>
                    {genMsg && (
                      <div
                        className={`px-2 py-1 rounded text-[9px] font-mono border ${
                          genMsg.ok
                            ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                            : "border-red-500/30 bg-red-500/[0.06] text-red-300"
                        }`}
                      >
                        {genMsg.text}
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            )}
          </div>

          {/* 报价列表 */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText size={11} className="text-amber-400" />
              <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
                报价记录（{quotes.length}）
              </span>
            </div>
            {quotes.length === 0 ? (
              <div className="py-8 text-center">
                <Users size={22} className="mx-auto text-gray-600 mb-2" />
                <p className="text-[11px] font-mono text-gray-500">暂无报价记录，选择客户生成首份报价单</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {quotes.map((q) => {
                  const isExpanded = expandedQuoteId === q.id;
                  const draft = negoDrafts[q.id] || { counter: "", note: "" };
                  return (
                    <div
                      key={q.id}
                      className="rounded-md border border-white/[0.04] bg-white/[0.02] overflow-hidden"
                    >
                      <button
                        onClick={() => setExpandedQuoteId(isExpanded ? null : q.id)}
                        className="w-full flex items-center gap-2.5 p-2.5 text-left hover:bg-white/[0.03] transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[12px] text-gray-200 truncate">{q.customerName}</span>
                            <span
                              className="px-1 rounded text-[8px] font-mono"
                              style={{ background: `${STATUS_COLOR[q.status]}20`, color: STATUS_COLOR[q.status] }}
                            >
                              {STATUS_LABEL[q.status]}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5 text-[9px] font-mono text-gray-500">
                            <span>{q.industry}</span>
                            <span>·</span>
                            <span>{q.tier}</span>
                            <span>·</span>
                            <span>{q.createdAt}</span>
                            {q.vaultPath && (
                              <>
                                <span>·</span>
                                <span className="text-cyan-500 truncate max-w-[180px]" title={q.vaultPath}>
                                  {q.vaultPath}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <div className="text-[12px] font-mono font-bold text-emerald-400">
                            {formatAmount(q.amount)}
                          </div>
                          <div className="text-[9px] font-mono text-violet-400">
                            成交 {q.winProbability}%
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronDown size={11} className="text-gray-500 flex-shrink-0" />
                        ) : (
                          <ChevronRight size={11} className="text-gray-500 flex-shrink-0" />
                        )}
                      </button>

                      {/* 展开内容：策略 + 谈判记录 + 复盘 */}
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="border-t border-white/[0.04] overflow-hidden"
                          >
                            <div className="p-2.5 space-y-2">
                              {/* 状态切换 */}
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-[9px] font-mono text-gray-500 uppercase mr-1">状态：</span>
                                {(["draft", "sent", "negotiating", "accepted", "rejected"] as QuoteStatus[]).map((s) => (
                                  <button
                                    key={s}
                                    onClick={() => updateQuoteStatus(q.id, s)}
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-colors ${
                                      q.status === s
                                        ? "bg-white/[0.08] text-white border border-white/15"
                                        : "text-gray-500 hover:text-gray-300 border border-transparent"
                                    }`}
                                  >
                                    {STATUS_LABEL[s]}
                                  </button>
                                ))}
                              </div>

                              {/* 谈判策略 */}
                              <div className="rounded bg-white/[0.02] border border-white/[0.04] p-2">
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Brain size={10} className="text-amber-400" />
                                  <span className="text-[9px] font-mono text-gray-500 uppercase">谈判策略</span>
                                </div>
                                <pre className="text-[10px] font-mono text-gray-300 whitespace-pre-wrap leading-snug">
                                  {q.strategy}
                                </pre>
                              </div>

                              {/* 谈判记录 */}
                              <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                  <Scale size={10} className="text-cyan-400" />
                                  <span className="text-[9px] font-mono text-gray-500 uppercase">
                                    谈判记录（{q.negotiations.length} 轮）
                                  </span>
                                </div>
                                {q.negotiations.length > 0 && (
                                  <div className="space-y-1 mb-2">
                                    {q.negotiations.map((n) => (
                                      <div
                                        key={n.id}
                                        className="rounded bg-white/[0.02] border border-white/[0.04] p-1.5 text-[10px] font-mono"
                                      >
                                        <div className="flex items-center gap-2 text-gray-300">
                                          <span className="text-cyan-400">第 {n.round} 轮</span>
                                          <span className="text-gray-600">·</span>
                                          <span>{n.date}</span>
                                        </div>
                                        <div className="text-gray-400 mt-0.5">
                                          我方 {formatAmount(n.ourOffer)} ↔ 客户还价 {formatAmount(n.customerCounter)}
                                        </div>
                                        {n.note && (
                                          <div className="text-gray-500 mt-0.5">{n.note}</div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* 新增谈判记录 */}
                                <div className="grid grid-cols-2 gap-1.5">
                                  <input
                                    value={draft.counter}
                                    onChange={(e) =>
                                      setNegoDrafts((s) => ({
                                        ...s,
                                        [q.id]: { ...draft, counter: e.target.value },
                                      }))
                                    }
                                    placeholder="客户还价金额"
                                    className="px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                                  />
                                  <input
                                    value={draft.note}
                                    onChange={(e) =>
                                      setNegoDrafts((s) => ({
                                        ...s,
                                        [q.id]: { ...draft, note: e.target.value },
                                      }))
                                    }
                                    placeholder="备注（如客户关注点）"
                                    className="px-2 py-1 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono focus:outline-none focus:border-cyan-500/40"
                                  />
                                </div>
                                <div className="flex items-center justify-end gap-2 mt-1.5">
                                  <button
                                    onClick={() => removeQuote(q.id)}
                                    className="px-2 py-0.5 rounded text-[9px] font-mono text-gray-500 hover:text-red-400"
                                  >
                                    删除报价
                                  </button>
                                  <button
                                    onClick={() => addNegotiation(q.id)}
                                    disabled={!draft.counter || Number(draft.counter) <= 0}
                                    className="flex items-center gap-1 px-2 py-1 rounded text-[9px] font-mono uppercase tracking-wider bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                  >
                                    <Plus size={10} />
                                    记录谈判
                                  </button>
                                </div>
                              </div>

                              {/* 复盘备注 */}
                              <div>
                                <div className="text-[9px] font-mono text-gray-500 uppercase mb-1">复盘备注</div>
                                <textarea
                                  value={q.reviewNote}
                                  onChange={(e) => updateReview(q.id, e.target.value)}
                                  placeholder="复盘：成功/失败原因、客户反馈、可改进点..."
                                  rows={2}
                                  className="w-full px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.06] text-[10px] text-gray-200 font-mono resize-none focus:outline-none focus:border-amber-500/40"
                                />
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
}

// ============================================
// KPI 小卡片
// ============================================
interface KpiCardProps {
  icon: typeof FileText;
  label: string;
  value: string;
  color: string;
}

function KpiCard({ icon: Icon, label, value, color }: KpiCardProps) {
  return (
    <div
      className="rounded-md border border-white/[0.06] bg-white/[0.02] px-2.5 py-1.5 flex items-center gap-2"
      style={{ borderLeft: `2px solid ${color}` }}
    >
      <Icon size={13} style={{ color }} className="flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider truncate">{label}</div>
        <div className="text-[13px] font-mono font-semibold truncate" style={{ color }}>
          {value}
        </div>
      </div>
    </div>
  );
}
